import { HttpClient, HttpHeaders } from '@angular/common/http';

import { environment } from '@environments/environment';
import { finalize } from 'rxjs/operators';
import { configMap } from '@app/shared/config-map';
import { MonoTypeOperatorFunction, Observable, pipe, UnaryFunction } from 'rxjs';
import { AddHttpParams } from './add-http-params';

import { Loading } from '@app/shared/models/loading.model';

export abstract class GenericService<T> {

  private _api = environment.api_url;

  constructor(private controller: string, private httpClient: HttpClient) {
    this.api = this.controller;
  }

  protected startLoading(loading: Loading): void {
    setTimeout(() => {
      loading = this.handlerLoading(loading);
      if (loading.startLoading) loading.load = true;
    }, 0);
  }

  protected stopLoading(loading: Loading): MonoTypeOperatorFunction<any> {
    loading = this.handlerLoading(loading);
    return loading.stopLoading ? finalize(() => loading.load = false) : pipe();
  }

  protected configMapAndLoading(loading: Loading): UnaryFunction<Observable<any>, Observable<any>> {
    return pipe(
      configMap(),
      this.stopLoading(loading)
    )
  }

  private handlerLoading(loading: Loading): Loading {
    if (loading == null || loading === undefined) loading = { startLoading: false, stopLoading: false };
    if (loading.startLoading === undefined) loading.startLoading = false;
    if (loading.stopLoading === undefined) loading.stopLoading = false;
    return loading;
  }

  protected getHttpClient(): HttpClient {
    return this.httpClient;
  }

  protected set api(controller: string) {
    this._api = `${this._api}/${controller}`;
  }

  protected get api(): string {
    return this._api;
  }

  get(loading?: Loading): Observable<T[]> {
    this.startLoading(loading);
    return this.getHttpClient()
      .get<T[]>(`${this._api}`)
      .pipe(this.configMapAndLoading(loading));
  }

  getWithParams(filter: T, loading?: Loading): Observable<T[]> {
    const params = new AddHttpParams(filter).createParams();
    this.startLoading(loading);
    return this.getHttpClient()
      .get<T[]>(`${this._api}`, { params })
      .pipe(this.configMapAndLoading(loading));
  }

  getById(id: number, loading?: Loading): Observable<T> {
    this.startLoading(loading);
    return this.getHttpClient()
      .get<T>(`${this._api}/${id}`)
      .pipe(this.configMapAndLoading(loading));
  }

  save(entity: T, loading?: Loading): Observable<T> {
    this.startLoading(loading);
    return this.getHttpClient()
      .post(`${this._api}`, entity)
      .pipe(this.configMapAndLoading(loading));
  }

  update(id: number, entity: T, loading?: Loading): Observable<T> {
    this.startLoading(loading);
    return this.getHttpClient().put(`${this._api}/${id}`, entity).pipe(this.configMapAndLoading(loading));
  }

  delete(id: number, loading?: Loading): Observable<void> {
    this.startLoading(loading);
    return this.getHttpClient()
      .delete(`${this._api}/${id}`)
      .pipe(this.configMapAndLoading(loading));
  }

  protected getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Set-Cookie': 'HttpOnly;Secure;SameSite=Strict',
    });
  }

  protected getHeadersPublico(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Set-Cookie': 'HttpOnly;Secure;SameSite=Strict',
      'skip': 'true'
    });
  }

  protected getHeadersTexto(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'text/html; charset=utf-8',
      'Accept': 'text/html; charset=utf-8',
      'skip': 'true'
    });
  }

  protected getHeadersPDF(): HttpHeaders {
    return new HttpHeaders().set('Accept', 'application/pdf');
  }

  protected getHeadersPDFPublico(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/pdf',
      'Accept': 'application/pdf',
      'Set-Cookie': 'HttpOnly;Secure;SameSite=Strict',
      'skip': 'true'
    });
  }
}
